import * as React from 'react';

import { useResizeObserver } from './useResizeObserver';

/**
 * Hook to track container width for responsive chart sizing
 * @param ref Optional ref to use. If not provided, a new ref will be created.
 */
export function useContainerWidth<T extends Element = HTMLDivElement>(
  ref?: React.RefObject<T | null | undefined>
): {
  containerWidth: number;
  containerRef: React.RefObject<T | null>;
} {
  const [containerWidth, setContainerWidth] = React.useState<number>(1000);
  const internalRef = React.useRef<T | null>(null);
  const containerRef = (ref ?? internalRef) as React.RefObject<T | null>;

  const updateWidth = React.useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }
  }, [containerRef]);

  // Check initially
  React.useEffect(() => {
    updateWidth();
  }, [updateWidth]);

  // Use useResizeObserver to watch for size changes
  useResizeObserver({
    ref: containerRef,
    onResize: updateWidth,
  });

  return { containerWidth, containerRef };
}
